import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useDropzone } from 'react-dropzone';
import toMoment from '~/lib/toMoment';
import isImage from 'shared/lib/isImage';
import isVideo from 'shared/lib/isVideo';
import { getS3AssetsManagement } from 'shared/lib/clientSettings';
import { flashMessage } from '~/app/actions';
import { convertedName, filterAssetData, isConverting, mimeTypes, sanitizeFileName } from '~/pages/components/S3AssetBrowser/assetUtils';
import useUploadManager from '~/pages/components/S3AssetBrowser/hooks/useUploadManager';
import type { Asset } from '~/pages/components/S3AssetBrowser/types';
import { S, A, TONES, DARK } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';
import Button from '~/app/ui/Button';
import { Page, PageHeader, Toolbar, Spacer } from '~/app/ui/Page';
import SearchField from '~/app/ui/SearchField';
import EmptyState from '~/app/ui/EmptyState';
import Dialog from '~/app/ui/Dialog';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import { useQueryParams } from '~/app/ui/useQueryParams';

// Same query as the editor's asset picker, so uploads refresh both.
const ASSETS = gql`
  query getAssetsAndFolders($path: String) {
    assets (path: $path) {
      Key
      Name: Key
      Size
      LastModified
    }
    folders (path: $path) {
      Name: Prefix
    }
  }
`;
// Every folder below the one open, searched on the server (it keeps the bucket's list; S3 can't search names).
const SEARCH = gql`
  query searchAssets($path: String, $query: String!) {
    searchAssets(path: $path, query: $query) {
      assets { Key Size LastModified }
      folders { Prefix }
      total
      truncated
    }
  }
`;
// One letter matches thousands of files: subfolders are searched from two.
const MIN_DEEP_SEARCH = 2;
const ADD_FOLDER = gql`
  mutation createAssetFolder($name: String!, $path: String!) {
    createAssetFolder(name: $name, path: $path) {
      Name: Prefix
    }
  }
`;

const FOLDER_NAME = /^[\w-]+$/;
const ACCEPT = [...mimeTypes.documents, ...mimeTypes.images, ...mimeTypes.videos];

const useStyles = createUseStyles({
  crumbs: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, margin: '-8px 0 16px', fontSize: 14 },
  crumb: {
    all: 'unset',
    padding: '4px 6px',
    borderRadius: 6,
    color: S.muted,
    cursor: 'pointer',
    '&:hover': { background: S.chromeHover, color: S.fg },
    '&:focus-visible': A.focus,
    '&[aria-current="page"]': { color: S.fg, fontWeight: 600, cursor: 'default', background: 'none' }
  },
  sep: { color: '#A5A8AE', [DARK]: { color: TONES.gray.dot } },
  layout: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 20, alignItems: 'start', '@media (max-width: 1000px)': { gridTemplateColumns: 'minmax(0, 1fr)' } },
  drop: { position: 'relative', minHeight: 320, borderRadius: 12 },
  dragging: { outline: `2px dashed ${S.fg}`, outlineOffset: 4, background: 'rgba(23, 26, 32, 0.03)', [DARK]: { background: TONES.gray.bg } },
  dropHint: {
    position: 'absolute',
    inset: 0,
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.85)',
    fontSize: 16,
    fontWeight: 600,
    [DARK]: { background: S.scrim }
  },
  // The overlay is as tall as all the files; its words stay in the middle of the window.
  dropHintText: { position: 'sticky', top: 'calc(50% - 12px)', bottom: 'calc(50% - 12px)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 },
  tile: {
    all: 'unset',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    background: A.surface,
    border: `1px solid ${S.line}`,
    borderRadius: 10,
    overflow: 'hidden',
    cursor: 'pointer',
    '&:hover': { borderColor: '#BFC4CB' },
    '&:focus-visible': A.focus,
    '&[aria-pressed="true"]': { borderColor: S.fg, boxShadow: `0 0 0 1px ${S.fg}` },
    '&[aria-busy="true"]': { cursor: 'progress', borderColor: S.line },
    [DARK]: { '&:hover': { borderColor: S.muted }, '&[aria-pressed="true"]': { borderColor: S.fg }, '&[aria-busy="true"]': { borderColor: S.line } }
  },
  thumb: {
    position: 'relative',
    height: 110,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `repeating-conic-gradient(${S.chrome} 0% 25%, ${A.surface} 0% 50%) 50% / 16px 16px`,
    color: S.muted,
    '& img': { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
    '& .material-icons': { fontSize: 36 }
  },
  folderThumb: { background: S.chrome, '& .material-icons': { fontSize: 44, color: '#8E949C' }, [DARK]: { '& .material-icons': { color: S.muted } } },
  tileName: { padding: '8px 10px', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // A search result's folder, under its name.
  // "in" stays; a long path is cut at its start, so the folder it's actually in stays in view.
  tileWhere: { display: 'flex', gap: 4, padding: '0 10px 8px', marginTop: -6, fontSize: 12, color: S.muted, whiteSpace: 'nowrap' },
  tileWherePath: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', direction: 'rtl', textAlign: 'left' },
  searchNote: { display: 'flex', alignItems: 'center', gap: 8, margin: '-4px 0 12px', fontSize: 13, color: S.muted },
  '@keyframes noteSpin': { to: { transform: 'rotate(360deg)' } },
  noteSpinner: { width: 11, height: 11, borderRadius: '50%', border: '2px solid currentColor', borderRightColor: 'transparent', animation: '$noteSpin 800ms linear infinite' },
  thumbSpinner: { width: 22, height: 22, borderRadius: '50%', border: '3px solid currentColor', borderRightColor: 'transparent', animation: '$noteSpin 800ms linear infinite' },
  folderLink: { all: 'unset', cursor: 'pointer', color: S.fg, textDecoration: 'underline', textUnderlineOffset: 2, '&:focus-visible': A.focus },
  panel: {
    position: 'sticky',
    top: 16,
    background: A.surface,
    border: `1px solid ${S.line}`,
    borderRadius: 12,
    overflow: 'hidden'
  },
  panelPreview: {
    height: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `repeating-conic-gradient(${S.chrome} 0% 25%, ${A.surface} 0% 50%) 50% / 16px 16px`,
    color: S.muted,
    '& img, & video': { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
    '& .material-icons': { fontSize: 48 }
  },
  panelBody: { display: 'grid', gap: 12, padding: 16 },
  panelName: { margin: 0, fontSize: 15, fontWeight: 600, wordBreak: 'break-all' },
  facts: { margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: 13, '& dt': { color: S.muted }, '& dd': { margin: 0 } },
  url: { boxSizing: 'border-box', width: '100%', padding: '8px 10px', border: `1px solid ${S.fieldLine}`, borderRadius: 8, fontFamily: S.mono, fontSize: 12, color: S.fg, background: S.chrome },
  panelActions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  panelEmpty: { padding: '28px 20px', fontSize: 14, color: S.muted, textAlign: 'center' },
  // An upload's progress: below the files, held at the bottom of the window however far down they're
  // scrolled (a drop can come from anywhere in a long folder). Graphite like the toasts; clicks pass through.
  uploadDock: { position: 'sticky', bottom: 20, zIndex: 4, display: 'flex', justifyContent: 'center', marginTop: 16, pointerEvents: 'none' },
  '@keyframes uploadIn': { from: { opacity: 0, transform: 'translateY(8px)' } },
  upload: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: 440,
    maxWidth: '100%',
    padding: '12px 16px',
    borderRadius: 10,
    fontSize: 14,
    background: S.fg,
    color: S.onFg,
    boxShadow: A.pop,
    animation: '$uploadIn 160ms ease-out',
    [DARK]: { background: A.surface, color: S.fg, boxShadow: `0 0 0 1px ${S.line}, ${A.pop}` }
  },
  uploadText: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  bar: { width: 96, flexShrink: 0, height: 4, borderRadius: 2, background: 'rgba(255, 255, 255, 0.25)', overflow: 'hidden', [DARK]: { background: S.chromeHover } },
  barFill: { display: 'block', height: '100%', background: 'currentColor', transition: 'width 300ms ease' },
  field: {
    boxSizing: 'border-box',
    width: '100%',
    height: 38,
    padding: '0 12px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: A.radius,
    fontFamily: S.ui,
    fontSize: 15,
    '&:focus': { outline: 'none', borderColor: S.fg, boxShadow: `0 0 0 1px ${S.fg}` }
  },
  fieldError: { margin: '6px 0 0', fontSize: 13, color: S.red },
  fieldHint: { margin: '6px 0 0', fontSize: 13, color: S.muted }
});

const formatSize = (bytes?: number | string | null) => {
  const n = Number(bytes);
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// "a/b/c.png" -> "a/b/"; "c.png" -> "".
const folderOf = (key: string) => key.slice(0, key.lastIndexOf('/') + 1);
const nameOf = (key: string) => key.slice(key.lastIndexOf('/') + 1);

const iconFor = (name: string) => (isVideo(name) ? 'movie' : isImage(name) ? 'image' : 'insert_drive_file');

/** An image tile that falls back to an icon if the file can't be loaded. */
const Thumb: React.FC<{ url: string; name: string }> = ({ url, name }) => {
  const [failed, setFailed] = useState(false);
  if (!isImage(name) || failed) return <i className="material-icons" aria-hidden>{iconFor(name)}</i>;
  return <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} />;
};

const AssetsScreen: React.FC = () => {
  const c = useStyles();
  const dispatch = useDispatch();
  const location = useLocation<{ path?: string } | undefined>();
  const [params, setParams] = useQueryParams();
  // The folder lives in the address (older links kept it in history state).
  const path = params.get('path') ?? location.state?.path ?? '';
  const base = getS3AssetsManagement() || '';
  const urlOf = useCallback((key: string) => `${base}/${encodeURI(key)}`, [base]);

  const { data, loading, error, refetch } = useQuery(ASSETS, { variables: { path }, fetchPolicy: 'cache-and-network' });
  const [addFolderMutation] = useMutation(ADD_FOLDER, { refetchQueries: ['getAssetsAndFolders'] });
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [folderDialog, setFolderDialog] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [toReplace, setToReplace] = useState<{ files: File[]; names: string[] } | null>(null);
  const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
  // Set when the person confirmed replacing files with the same name (Genesis refuses it otherwise).
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);

  useEffect(() => { setSelected(''); setQ(''); }, [path]);
  // The search field sends once typing pauses, or at once on Enter.
  const searchFor = q.trim().length >= MIN_DEEP_SEARCH ? q.trim() : '';
  const deep = useQuery(SEARCH, { variables: { path, query: searchFor }, skip: !searchFor, fetchPolicy: 'cache-and-network' });

  const onError = useCallback((message: string) => dispatch(flashMessage(message, 'ERROR', 8000)), [dispatch]);
  const { uploading, progress, uploaded } = useUploadManager({ path, files: filesToUpload, onError, replace: replaceConfirmed });
  const uploadName = filesToUpload.length === 1 ? filesToUpload[0].name : `${filesToUpload.length} files`;

  const { assets, folders } = useMemo(() => filterAssetData(data, path), [data, path]);
  const needle = q.trim().toLowerCase();
  // Searching: what's in this folder shows at once; the search below it replaces that when it answers.
  const found = searchFor && deep.data ? deep.data.searchAssets : null;
  const searching = Boolean(searchFor) && !found && !deep.error;
  const shownFolders: Array<{ Name: string; where?: string }> = found
    ? found.folders.map((f: { Prefix: string }) => ({ Name: f.Prefix.slice(path.length), where: folderOf(f.Prefix.slice(path.length, -1)) }))
    : folders.filter(f => !needle || f.Name.toLowerCase().includes(needle));
  const shownAssets: Array<Asset & { where?: string }> = found
    ? found.assets.map((a: Asset) => ({ ...a, Name: nameOf(a.Key), where: folderOf(a.Key.slice(path.length)) }))
    : (assets as Asset[]).filter(a => !needle || a.Name.toLowerCase().includes(needle));
  const current = shownAssets.find(a => a.Key === selected) || (assets as Asset[]).find(a => a.Key === selected);
  const currentFolder = current ? folderOf(current.Key) : path;

  // When an upload finishes, select its file (the first, if several) so its preview and address show
  // at once, and bring its tile into view once it's on screen.
  const [reveal, setReveal] = useState('');
  useEffect(() => {
    if (!uploaded?.length) return;
    setSelected(uploaded[0]);
    setReveal(uploaded[0]);
  }, [uploaded]);
  useEffect(() => {
    if (!reveal || current?.Key !== reveal) return;
    document.querySelector('[data-testid="asset-grid"] [aria-pressed="true"]')?.scrollIntoView({ block: 'nearest' });
    setReveal('');
  }, [reveal, current]);

  // An image still being optimized with no upload here polling for it (uploaded in another tab, or
  // before this folder was opened): look again every few seconds till it's done, or stops counting.
  const [, setTick] = useState(0);
  const waitingOnConvert = !uploading && shownAssets.some(a => isConverting(a));
  const refetchShown = found ? deep.refetch : refetch;
  useEffect(() => {
    if (!waitingOnConvert) return undefined;
    const id = window.setInterval(() => {
      setTick(t => t + 1);
      refetchShown().catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(id);
  }, [waitingOnConvert, refetchShown]);

  const goTo = (next: string) => setParams({ path: next || null });
  const segments = path.split('/').filter(Boolean);

  // Uploading a file with the name of one already here replaces it; ask first. Names are
  // compared as they're stored ("My Photo.png" is saved as "My-Photo.png").
  const startUpload = (files: File[]) => {
    const names = files.filter((file) => {
      const stored = sanitizeFileName(file.name);
      const transformed = `${stored.replace(/\.[^/.]+$/, '')}-default.jpg`;
      return (assets as Asset[]).some(a => a.Name === stored || a.Name === transformed);
    }).map(f => f.name);
    if (names.length) setToReplace({ files, names });
    else {
      setReplaceConfirmed(false);
      setFilesToUpload(files);
    }
  };

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    accept: ACCEPT,
    noClick: true,
    noKeyboard: true,
    onDrop: (accepted: File[], rejected: unknown[]) => {
      if (rejected.length) {
        // Rejections are files in older versions of react-dropzone, { file } in newer ones.
        const names = rejected.map((r) => {
          const item = r as { name?: string; file?: { name: string } };
          return item.file ? item.file.name : item.name;
        });
        dispatch(flashMessage(`These file types can’t be uploaded:\n${names.join('\n')}`, 'ERROR'));
        return;
      }
      if (accepted.length) startUpload(accepted);
    }
  });

  const createFolder = async () => {
    const name = folderName.trim();
    if (!FOLDER_NAME.test(name)) return;
    try {
      await addFolderMutation({ variables: { name, path } });
      setFolderDialog(false);
      setFolderName('');
      dispatch(flashMessage(`Created the folder “${name}”.`));
    } catch (e) {
      dispatch(flashMessage((e as Error).message, 'ERROR'));
    }
  };

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      dispatch(flashMessage('Your browser didn’t allow copying. Select the address and copy it.', 'ERROR'));
    }
  };

  const nameValid = FOLDER_NAME.test(folderName.trim());

  let content: React.ReactNode;
  if (error && !data) {
    content = <EmptyState tone="error" title="Couldn’t load assets" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.graphQLErrors?.[0]?.message || error.message}</EmptyState>;
  } else if (loading && !data) {
    content = <EmptyState icon="hourglass_empty" title="Loading…" compact />;
  } else if (shownFolders.length === 0 && shownAssets.length === 0) {
    content = needle && searching
      ? <EmptyState icon="manage_search" title="Searching…" compact />
      : needle
      ? <EmptyState icon="search_off" title={`Nothing ${!searchFor ? 'in this folder' : path ? 'in this folder or its subfolders' : 'in any folder'} matches “${q.trim()}”`} action={<Button onClick={() => setQ('')}>Clear search</Button>} />
      : <EmptyState icon="perm_media" title="This folder is empty" action={<Button variant="primary" icon="upload" onClick={open}>Upload files</Button>}>Drop files here, or upload them.</EmptyState>;
  } else {
    content = (
      <div className={c.grid} data-testid="asset-grid">
        {shownFolders.map(f => (
          <button key={f.Name} type="button" className={c.tile} onClick={() => goTo(`${path}${f.Name}`)} data-folder={f.Name}>
            <span className={`${c.thumb} ${c.folderThumb}`}><i className="material-icons" aria-hidden>folder</i></span>
            <span className={c.tileName} title={f.Name}>{nameOf(f.Name.replace(/\/$/, ''))}</span>
            {found && <span className={c.tileWhere} title={`/${path}${f.where}`}>in <span className={c.tileWherePath}>{f.where ? `${f.where.replace(/\/$/, '')}\u200E` : 'this folder'}</span></span>}
          </button>
        ))}
        {shownAssets.map((a) => {
          // A just-uploaded image's address stops working once its compressed copy replaces it,
          // so it shows under the copy's name and can't be picked (or its address copied) till then.
          if (isConverting(a, { uploading })) {
            const name = convertedName(a.Name);
            return (
              <button key={a.Key} type="button" className={c.tile} disabled aria-busy="true" data-asset={a.Name} data-converting="true">
                <span className={c.thumb}><span className={c.thumbSpinner} aria-hidden /></span>
                <span className={c.tileName} title={name}>{name}</span>
                <span className={c.tileWhere}>Optimizing…</span>
              </button>
            );
          }
          return (
            <button key={a.Key} type="button" className={c.tile} aria-pressed={selected === a.Key} onClick={() => setSelected(a.Key)} data-asset={a.Name}>
              <span className={c.thumb}><Thumb url={urlOf(a.Key)} name={a.Name} /></span>
              <span className={c.tileName} title={a.Name}>{a.Name}</span>
              {found && <span className={c.tileWhere} title={`/${path}${a.where}`}>in <span className={c.tileWherePath}>{a.where ? `${a.where.replace(/\/$/, '')}\u200E` : 'this folder'}</span></span>}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <Page testId="assets-screen">
      <PageHeader
        title={tr('Assets', '素材')}
        description="Images, videos and files for pages. Drop files anywhere below to upload them into this folder."
        actions={(
          <>
            <Button icon="create_new_folder" onClick={() => { setFolderName(''); setFolderDialog(true); }} data-testid="new-folder">New folder</Button>
            <Button variant="primary" icon="upload" onClick={open} busy={uploading} data-testid="upload">Upload</Button>
          </>
        )}
      />
      <nav className={c.crumbs} aria-label="Folder">
        <button type="button" className={c.crumb} aria-current={segments.length === 0 ? 'page' : undefined} onClick={() => goTo('')}>All assets</button>
        {segments.map((s, i) => {
          const to = `${segments.slice(0, i + 1).join('/')}/`;
          const last = i === segments.length - 1;
          return (
            <React.Fragment key={to}>
              <span className={c.sep} aria-hidden>/</span>
              <button type="button" className={c.crumb} aria-current={last ? 'page' : undefined} onClick={() => { if (!last) goTo(to); }}>{s}</button>
            </React.Fragment>
          );
        })}
      </nav>
      <Toolbar>
        <SearchField value={q} onChange={setQ} delay={250} placeholder={path ? 'Search this folder and its subfolders' : 'Search all assets'} testId="assets-search" />
        <Spacer />
      </Toolbar>
      {needle && (
        <div className={c.searchNote} role="status" data-testid="assets-search-note">
          {!searchFor && `Showing matches in this folder. Type ${MIN_DEEP_SEARCH} or more characters to search ${path ? 'its subfolders' : 'every folder'} too.`}
          {searching && <><span className={c.noteSpinner} aria-hidden />Searching {path ? 'subfolders' : 'every folder'}…</>}
          {!searching && found && (found.truncated
            ? `Showing the best ${found.assets.length + found.folders.length} of ${found.total.toLocaleString()} matches. Type more to narrow it down.`
            : `${found.total.toLocaleString()} ${found.total === 1 ? 'match' : 'matches'} ${path ? 'in this folder and its subfolders' : 'in all assets'}`)}
          {deep.error && `Couldn’t search subfolders (${deep.error.message}). Showing this folder only.`}
        </div>
      )}
      <div className={c.layout}>
        <div {...getRootProps({ className: `${c.drop} ${isDragActive ? c.dragging : ''}` })} data-testid="asset-drop">
          <input {...getInputProps()} />
          {content}
          {uploading && (
            <div className={c.uploadDock}>
              <div className={c.upload} role="status" data-testid="upload-status">
                <span className={c.noteSpinner} aria-hidden />
                <span className={c.uploadText} title={uploadName}>{(progress || 0) >= 1 ? 'Processing' : 'Uploading'} {uploadName}…</span>
                <span className={c.bar}><span className={c.barFill} style={{ width: `${Math.round((progress || 0.05) * 100)}%` }} /></span>
              </div>
            </div>
          )}
          {isDragActive && <div className={c.dropHint}><span className={c.dropHintText}>Drop to upload into {segments.length ? segments[segments.length - 1] : 'All assets'}</span></div>}
        </div>
        <aside className={c.panel} aria-label="Selected file" data-testid="asset-details">
          {current ? (
            <>
              <div className={c.panelPreview}>
                {isVideo(current.Name)
                  ? <video src={urlOf(current.Key)} controls muted />
                  : <Thumb key={current.Key} url={urlOf(current.Key)} name={current.Name} />}
              </div>
              <div className={c.panelBody}>
                <h2 className={c.panelName}>{current.Name}</h2>
                <dl className={c.facts}>
                  <dt>Size</dt><dd>{formatSize(current.Size)}</dd>
                  <dt>Updated</dt><dd>{toMoment(current.LastModified)?.format('MMM D, YYYY h:mm A') || '—'}</dd>
                  <dt>Folder</dt>
                  <dd>
                    {currentFolder === path
                      ? `/${path}`
                      : <button type="button" className={c.folderLink} onClick={() => goTo(currentFolder)} data-testid="asset-open-folder">/{currentFolder}</button>}
                  </dd>
                </dl>
                <input className={c.url} readOnly value={urlOf(current.Key)} onFocus={e => e.target.select()} aria-label="File address" />
                <div className={c.panelActions}>
                  <Button variant="primary" icon={copied ? 'check' : 'content_copy'} onClick={() => copyUrl(urlOf(current.Key))} data-testid="copy-url">{copied ? 'Copied' : 'Copy address'}</Button>
                  <Button icon="open_in_new" href={urlOf(current.Key)} target="_blank">Open</Button>
                </div>
              </div>
            </>
          ) : (
            <div className={c.panelEmpty}>Select a file to see its details and copy its address.</div>
          )}
        </aside>
      </div>

      {folderDialog && (
        <Dialog
          title="New folder"
          onClose={() => setFolderDialog(false)}
          width={440}
          testId="folder-dialog"
          footer={(
            <>
              <Button onClick={() => setFolderDialog(false)}>Cancel</Button>
              <Button variant="primary" onClick={createFolder} disabled={!nameValid} data-testid="create-folder">Create folder</Button>
            </>
          )}
        >
          <label htmlFor="new-folder-name" style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 }}>Name</label>
          <input
            id="new-folder-name"
            className={c.field}
            value={folderName}
            autoFocus
            onChange={e => setFolderName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); }}
          />
          {folderName.trim() && !nameValid
            ? <p className={c.fieldError} role="alert">Use letters, numbers, - and _ only.</p>
            : <p className={c.fieldHint}>Inside /{path}. Letters, numbers, - and _.</p>}
        </Dialog>
      )}
      {toReplace && (
        <ConfirmDialog
          danger
          title={`Replace ${toReplace.names.length === 1 ? 'a file' : `${toReplace.names.length} files`} with the same name?`}
          confirmLabel="Replace"
          onConfirm={() => { setReplaceConfirmed(true); setFilesToUpload(toReplace.files); }}
          onClose={() => setToReplace(null)}
        >
          <p>Uploading replaces {toReplace.names.length === 1 ? 'this file' : 'these files'} in /{path}:</p>
          <ul style={{ margin: 0, paddingLeft: 18, fontFamily: S.mono, fontSize: 12 }}>{toReplace.names.map(n => <li key={n}>{n}</li>)}</ul>
        </ConfirmDialog>
      )}
    </Page>
  );
};

export default AssetsScreen;
